/**
 * A curva do saldo do jogo, partida a partida.
 *
 * Números soltos — 12 partidas, 7 vitórias, +R$ 40 — não respondem a
 * pergunta que a pessoa realmente faz, que é "estou melhorando ou
 * piorando?". Sete vitórias em doze parece bom até se ver que as cinco
 * derrotas foram as cinco últimas.
 *
 * Desenhado à mão em SVG, e não com Recharts: aquilo custa perto de 100 kB
 * comprimidos e traz um sistema de eixos, legendas e temas que teria de
 * ser desligado peça por peça para caber neste visual. Aqui são quarenta
 * linhas e nenhum download a mais.
 *
 * Duas decisões que valem dizer:
 *
 * A linha do zero é sempre visível e marcada. Sem ela, uma sequência de
 * derrotas desenha uma curva descendente bonita que não deixa claro em
 * que momento a pessoa passou a estar no prejuízo — e esse é o único
 * ponto do gráfico que muda uma decisão.
 *
 * A área abaixo da curva é pintada com a cor do desfecho atual: verde
 * quando o acumulado está positivo, vermelho quando está negativo. Não é
 * enfeite; é o que se lê antes de olhar qualquer número.
 */

export interface PontoTrajetoria {
  /** Acumulado em centavos, com sinal, depois desta partida. */
  readonly acumuladoCents: number;
}

export function Trajetoria({
  pontos,
  altura = 96,
}: {
  pontos: readonly PontoTrajetoria[];
  altura?: number;
}) {
  // Menos de duas partidas não desenha linha nenhuma: um ponto isolado
  // não é trajetória, e um gráfico vazio com eixos parece defeito.
  if (pontos.length < 2) return null;

  const valores = pontos.map((p) => p.acumuladoCents);
  const maximo = Math.max(...valores, 0);
  const minimo = Math.min(...valores, 0);
  const amplitude = maximo - minimo || 1;

  const largura = 100;
  const y = (v: number): number => altura - ((v - minimo) / amplitude) * altura;
  const x = (i: number): number => (i / (pontos.length - 1)) * largura;

  const linha = valores.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ');
  const area = `${linha} L ${largura} ${y(minimo)} L 0 ${y(minimo)} Z`;

  const final = valores[valores.length - 1] ?? 0;
  const cor = final < 0 ? 'var(--danger)' : 'var(--primary)';
  const zero = y(0);

  return (
    <svg
      viewBox={`0 0 ${largura} ${altura}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height: altura }}
      role="img"
      aria-label={`Trajetória do saldo ao longo de ${pontos.length} partidas`}
    >
      <defs>
        <linearGradient id="trajetoria-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={cor} stopOpacity="0.22" />
          <stop offset="100%" stopColor={cor} stopOpacity="0" />
        </linearGradient>
      </defs>

      <path d={area} fill="url(#trajetoria-area)" />

      {/* A linha do zero: onde o lucro vira prejuízo. */}
      <line
        x1="0"
        y1={zero}
        x2={largura}
        y2={zero}
        stroke="var(--muted-foreground)"
        strokeWidth="0.5"
        strokeDasharray="2 2"
        vectorEffect="non-scaling-stroke"
      />

      <path
        d={linha}
        fill="none"
        stroke={cor}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* O ponto de agora, que é onde o olho para. */}
      <circle
        cx={x(pontos.length - 1)}
        cy={y(final)}
        r="3"
        fill={cor}
        stroke="var(--card)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
